import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Button, LinearProgress, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import type { IDBPDatabase } from 'idb';

import {
  fetchGazeResultDetail,
  fetchGazeResults,
  finalizeChunkedUpload,
  normalizeSamplesForUpload,
  submitVideoSession,
  submitChunk,
  submitForm,
  uploadSamplesSequential,
} from '../api';
import type { VideoSubmitProgress } from '../api';
import { iterateKeys } from '../runner/iterateKeys';
import { deleteVideoArtifact, openOrCreateDB, readVideoArtifact, MyDB } from '../runner/openIDB';
import { computeMaxPayloadBytes, gzipPayload, toPositiveInt } from '../runner/uploadUtils';
import { useSession } from '../store/session';
import type {
  ArgusCaptureQcV1,
  ArgusCaptureTelemetryV1,
  ArgusCollectionDraftV1,
  EyeTraxSample,
  TimelineSeg,
} from '../types';
import {
  buildArgusRunnerPath,
  isVideoWorkflow,
  persistArgusWorkflow,
  resolveArgusWorkflow,
} from '../lib/workflow';
import { clearClientRunId, getOrCreateClientRunId } from '../lib/clientRun';

interface TaskResult {
  task: string;
  label: string;
  confidence: number;
  prob?: number;
  threshold?: number;
}

interface DiagnosisResult {
  id?: number;
  label?: string;
  confidence?: number | null;
  tasks?: TaskResult[];
  name?: string;
  state?: string;
  route?: string;
}

interface UploadProgressInfo {
  sentChunks: number;
  totalChunks: number;
  bytes: number;
}

type LocalScreeningSummary = {
  label: string;
  confidence: number | null;
  tasks: TaskResult[];
  meta: Record<string, unknown>;
};

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const DEBUG_ENABLED = process.env.NODE_ENV !== 'production';

const logDebug = (message: string, payload?: unknown) => {
  if (!DEBUG_ENABLED) return;
  if (payload !== undefined) {
    console.debug(message, payload);
  } else {
    console.debug(message);
  }
};

const FLUSH_BATCH_SIZE = toPositiveInt(import.meta.env.VITE_UPLOAD_BATCH_SIZE, 250);
const FLUSH_CONCURRENCY = Math.max(1, toPositiveInt(import.meta.env.VITE_UPLOAD_CONCURRENCY, 8));
const FLUSH_QUEUE_CAP = Math.max(FLUSH_CONCURRENCY * 2, FLUSH_CONCURRENCY + 1);
const FLUSH_BACKPRESSURE_MS = toPositiveInt(import.meta.env.VITE_UPLOAD_BACKPRESSURE_DELAY_MS, 20);
const MAX_PAYLOAD_BYTES = computeMaxPayloadBytes(import.meta.env.VITE_UPLOAD_MAX_BYTES);
const DEMO_FAKE_VIDEO_UPLOAD =
  import.meta.env.DEV &&
  ['1', 'true', 'yes', 'on'].includes(
    String(import.meta.env.VITE_ARGUS_FAKE_VIDEO_UPLOAD ?? '').toLowerCase(),
  );
const LOCAL_RESULT_DETAIL_KEY = 'argus:local_result_detail';

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const readNumber = (key: string): number => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch (error) {
    logDebug(`[Done] failed to read numeric localStorage key ${key}`, error);
    return 0;
  }
};

const readStaticInfo = () => {
  try {
    const raw = localStorage.getItem('argus_static');
    return raw ? JSON.parse(raw) : undefined;
  } catch (error) {
    logDebug('[Done] failed to parse argus_static', error);
    return undefined;
  }
};

const readCollectionContext = (): ArgusCollectionDraftV1 | undefined => {
  try {
    const raw = localStorage.getItem('argus_collection_context');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as ArgusCollectionDraftV1;
    if (parsed?.version !== 1) return undefined;
    return parsed;
  } catch (error) {
    logDebug('[Done] failed to parse argus_collection_context', error);
    return undefined;
  }
};

async function flushPendingIDB(
  formDataId: number,
  onFlushed?: (count: number) => void,
): Promise<number> {
  let db: IDBPDatabase<MyDB> | null = null;
  try {
    db = await openOrCreateDB();
  } catch (error) {
    logDebug('[Done] failed to open IndexedDB for flush', error);
    return 0;
  }

  if (!db) return 0;

  const queue: number[][] = [];
  const sleepers: Array<() => void> = [];
  const wake = () => {
    while (sleepers.length) {
      const resolver = sleepers.shift();
      if (resolver) resolver();
    }
  };

  let producerDone = false;
  let total = 0;

  const producer = (async () => {
    try {
      for await (const keys of iterateKeys(db!, formDataId, FLUSH_BATCH_SIZE)) {
        if (!keys.length) continue;
        while (queue.length >= FLUSH_QUEUE_CAP) {
          // eslint-disable-next-line no-await-in-loop
          await delay(FLUSH_BACKPRESSURE_MS);
        }
        queue.push(keys);
        wake();
      }
    } finally {
      producerDone = true;
      wake();
    }
  })();

  const worker = async () => {
    while (!producerDone || queue.length > 0) {
      const keys = queue.shift();
      if (!keys) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => sleepers.push(resolve));
        continue;
      }

      try {
        const readTx = db!.transaction('landmarks', 'readonly');
        const store = readTx.objectStore('landmarks');
        const records = await Promise.all(keys.map((key) => store.get(key)));
        await readTx.done;

        const available: Array<{ key: number; record: MyDB['landmarks']['value'] }> = [];
        const missing: number[] = [];
        records.forEach((record, index) => {
          const key = keys[index];
          if (record) {
            available.push({ key, record });
          } else {
            missing.push(key);
          }
        });

        if (available.length > 0) {
          const uploadItems = available.map(({ key, record }) => ({
            key,
            sample: {
              timestamp: Number(record.timestamp),
              mediaIndex: Number(record.mediaIndex),
              landmarks: record.landmarks,
            } satisfies EyeTraxSample,
          }));

          const chunks: typeof uploadItems[] = [uploadItems];
          while (chunks.length) {
            const chunk = chunks.shift();
            if (!chunk || chunk.length === 0) continue;
            const seq = Math.max(0, Math.trunc(Number(chunk[0]?.key ?? 0)));
            const samples = chunk.map((item) => item.sample);
            const normalizedChunk = normalizeSamplesForUpload(samples);
            const preview = {
              formDataId,
              seq,
              final: false as const,
              samples: normalizedChunk,
            };
            const estimated = gzipPayload(preview).byteLength;
            if (estimated > MAX_PAYLOAD_BYTES && chunk.length > 1) {
              const mid = Math.ceil(chunk.length / 2);
              chunks.unshift(chunk.slice(mid));
              chunks.unshift(chunk.slice(0, mid));
              continue;
            }

            await submitChunk(formDataId, normalizedChunk, seq);
            total += chunk.length;
            onFlushed?.(chunk.length);
          }

          const deleteTx = db!.transaction('landmarks', 'readwrite');
          const deleteStore = deleteTx.objectStore('landmarks');
          await Promise.all(available.map(({ key }) => deleteStore.delete(key)));
          if (missing.length) {
            await Promise.all(missing.map((key) => deleteStore.delete(key)));
          }
          await deleteTx.done;
        } else if (missing.length) {
          const deleteTx = db!.transaction('landmarks', 'readwrite');
          const deleteStore = deleteTx.objectStore('landmarks');
          await Promise.all(missing.map((key) => deleteStore.delete(key)));
          await deleteTx.done;
        }
      } catch (error) {
        logDebug('[Done] flush worker failed', error);
        throw error;
      }
    }
  };

  try {
    await Promise.all([producer, ...Array.from({ length: FLUSH_CONCURRENCY }, worker)]);
  } finally {
    db.close();
  }

  return total;
}

const closeWindow = (navigate: ReturnType<typeof useNavigate>, result: DiagnosisResult | null) => {
  const targetPath = result?.route ?? (result?.id ? `/results/${result.id}` : '/dashboard');
  try {
    const channel = new BroadcastChannel('argus-runner');
    channel.postMessage({ type: 'argus:test:done', result });
    channel.close();
  } catch (error) {
    logDebug('[Done] broadcast channel failure', error);
  }

  try {
    localStorage.setItem('argus:test:done', Date.now().toString());
    if (result) {
      localStorage.setItem('argus:last_result', JSON.stringify(result));
    }
    localStorage.setItem('argus:please-close', Date.now().toString());
  } catch (error) {
    logDebug('[Done] failed to persist completion markers', error);
  }

  try {
    window.close();
    if (!window.closed) {
      window.open('', '_self');
      window.close();
    }
    logDebug('[Done] attempted window close', window.closed);
  } catch (error) {
    logDebug('[Done] window.close error', error);
  }

  window.setTimeout(() => {
    if (!window.closed) {
      logDebug('[Done] fallback navigate to result page', targetPath);
      navigate(targetPath, { replace: true });
    }
  }, 200);
};

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

const randomUnit = (): number => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const value = new Uint32Array(1);
      crypto.getRandomValues(value);
      return value[0] / 0xffffffff;
    }
  } catch {
    // Fall through to Math.random.
  }
  return Math.random();
};

const buildLocalResultId = (): number => {
  const suffix = Math.floor(randomUnit() * 1000);
  return Date.now() * 1000 + suffix;
};

const buildDemoScreeningSummary = (collectionContext: ArgusCollectionDraftV1): LocalScreeningSummary => {
  const intake = collectionContext.intake;
  const sex = intake.sex === 'M' ? 1 : 0;
  const ageTerm = (Number(intake.age) - 68) / 10;
  const eduTerm = (Number(intake.education) - 4) / 1.75;
  const score = -1.35 + 0.78 * ageTerm + 0.18 * sex - 0.42 * eduTerm;
  const baselineProb = 1 / (1 + Math.exp(-score));
  const jitter = (randomUnit() - 0.5) * 0.05;
  const prob = clamp01(baselineProb + jitter);
  const threshold = 0.5;
  const label = prob >= threshold ? 'At Risk' : 'Low Risk';
  const confidence = label === 'At Risk' ? prob : 1 - prob;
  return {
    label,
    confidence,
    tasks: [
      {
        task: '综合筛查结果',
        label,
        confidence,
        prob,
        threshold,
      },
    ],
    meta: {
      resultMode: 'general_screening_summary',
      resultMethod: 'local_demographics_summary_v1',
      fitSource: 'local_demographics_no_upload',
      baselineProb,
      probabilityJitter: jitter,
      threshold,
    },
  };
};

const buildMissingCaptureQc = (): ArgusCaptureQcV1 => ({
  version: 1,
  passed: false,
  reason: 'missing_capture_qc',
  totalFrames: 0,
  facePresentFrames: 0,
  facePresentRatio: 0,
  multiFaceFrames: 0,
  longestNoFaceMs: 0,
  minFacePresentRatio: 0.8,
  maxAllowedNoFaceMs: 10_000,
  minSegmentFaceRatio: 0.6,
  maxFailedSegmentRatio: 1,
  failedSegmentCount: 0,
  segmentCount: 0,
  segments: [],
});

const buildQcFailedSummary = (captureQc: ArgusCaptureQcV1): LocalScreeningSummary => ({
  label: '',
  confidence: null,
  tasks: [],
  meta: {
    resultMode: 'qc_failed_no_screening_result',
    resultMethod: 'local_capture_qc_gate_v1',
    fitSource: 'local_demographics_no_upload',
    qcPassed: false,
    qcReason: captureQc.reason ?? 'client_qc_failed',
    minFacePresentRatio: captureQc.minFacePresentRatio,
  },
});

const persistDemoResultDetail = (
  collectionContext: ArgusCollectionDraftV1,
  clientRunId: string,
  workflow: ReturnType<typeof resolveArgusWorkflow>,
  captureQc: ArgusCaptureQcV1,
  captureTelemetry: ArgusCaptureTelemetryV1 | null,
  timeline: TimelineSeg[],
  summary: LocalScreeningSummary,
): number => {
  const localResultId = buildLocalResultId();
  const status = captureQc.passed ? 'local_generated' : 'local_qc_failed';
  try {
    localStorage.setItem(
      LOCAL_RESULT_DETAIL_KEY,
      JSON.stringify({
        id: localResultId,
        name: collectionContext.participant.name ?? undefined,
        label: summary.label,
        confidence: summary.confidence,
        timestamp: new Date().toISOString(),
        status,
        captureMode: 'video',
        static: collectionContext.intake,
        tasks: summary.tasks,
        timeline,
        source: 'local_demographic_summary',
        meta: {
          ...summary.meta,
          localDemographicResult: true,
          fakeVideoUpload: true,
          workflowLabel: workflow.label,
          workflowId: workflow.workflowId,
          protocolId: workflow.protocolId,
          clientRunId,
          captureTelemetry,
        },
        qc: captureQc,
        participant: {
          id: 0,
          clinicParticipantId: collectionContext.participant.clinicParticipantId,
          name: collectionContext.participant.name ?? null,
        },
        officialDiagnosis: collectionContext.officialDiagnosis,
        video: null,
      }),
    );
    localStorage.removeItem('argus:demo_result_detail');
  } catch (error) {
    logDebug('[Done] failed to persist local demographic result detail', error);
  }
  return localResultId;
};

export default function Done(): JSX.Element {
  const location = useLocation();
  const nav = useNavigate();
  const workflow = useMemo(() => resolveArgusWorkflow(location.search), [location.search]);
  const {
    samples,
    clear,
    timeline,
    videoBlob,
    videoMimeType,
    videoBytes,
    captureQc,
    captureTelemetry,
  } = useSession();

  useEffect(() => {
    persistArgusWorkflow(workflow);
  }, [workflow]);

  const ran = useRef(false);
  const isMountedRef = useRef(true);
  const latestKnownResultIdRef = useRef<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DiagnosisResult | null>(null);
  const [progress, setProgress] = useState<UploadProgressInfo | null>(null);
  const [videoProgress, setVideoProgress] = useState<VideoSubmitProgress | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [recoveredVideo, setRecoveredVideo] = useState<{
    blob: Blob;
    mimeType: string | null;
    bytes: number;
  } | null>(null);

  const clientRunId = useMemo(() => getOrCreateClientRunId(), []);
  const persistedFormId = useMemo(() => readNumber('argus_formDataId'), []);
  const staticInfo = useMemo(() => readStaticInfo(), []);
  const collectionContext = useMemo(() => readCollectionContext(), []);
  const effectiveVideoBlob = videoBlob ?? recoveredVideo?.blob ?? null;
  const effectiveVideoMimeType = videoMimeType ?? recoveredVideo?.mimeType ?? null;
  const effectiveVideoBytes =
    videoBytes > 0 ? videoBytes : recoveredVideo?.bytes ?? effectiveVideoBlob?.size ?? 0;
  const attachName = useCallback(
    (payload: DiagnosisResult): DiagnosisResult => {
      let rawName: unknown = undefined;
      if (collectionContext?.participant?.name) {
        rawName = collectionContext.participant.name;
      }
      if (staticInfo && typeof staticInfo === 'object') {
        const staticName = (staticInfo as Record<string, unknown>)['name'];
        if (typeof staticName === 'string' && staticName.trim().length > 0) {
          rawName = staticName;
        }
      }
      if (typeof rawName === 'string') {
        const trimmed = rawName.trim();
        if (trimmed.length > 0) {
          return {
            ...payload,
            name: trimmed,
            tasks: payload.tasks?.filter((task) => task.task.toUpperCase() !== 'CDR'),
          };
        }
      }
      return {
        ...payload,
        tasks: payload.tasks?.filter((task) => task.task.toUpperCase() !== 'CDR'),
      };
    },
    [collectionContext, staticInfo],
  );

  const clearCollectionStorage = useCallback(() => {
    try {
      localStorage.removeItem('argus_collection_context');
    } catch (error) {
      logDebug('[Done] failed to clear argus_collection_context', error);
    }
  }, []);

  const loadLatestResultId = useCallback(async (): Promise<number | null> => {
    try {
      const results = await fetchGazeResults();
      if (!Array.isArray(results) || results.length === 0) {
        latestKnownResultIdRef.current = null;
        return null;
      }
      const latest = results[0];
      const id = Number((latest as { id?: unknown }).id);
      const resolved = Number.isFinite(id) ? id : null;
      latestKnownResultIdRef.current = resolved;
      return resolved;
    } catch (loadError) {
      logDebug('[Done] failed to load latest result id', loadError);
      return latestKnownResultIdRef.current ?? null;
    }
  }, []);

  const isRecoverableError = (reason: unknown): boolean => {
    if (!(reason instanceof Error)) return false;
    const msg = reason.message || '';
    return [
      '499',
      'NetworkError',
      'Failed to fetch',
      'The network connection was lost',
      'Load failed',
    ].some((fragment) => msg.includes(fragment));
  };

  const pollForNewResult = useCallback(
    async (baselineId: number | null, startedAt: number): Promise<DiagnosisResult | null> => {
      const deadline = Date.now() + 90_000;
      while (Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await delay(4_000);
        try {
          const results = await fetchGazeResults();
          if (!Array.isArray(results) || results.length === 0) continue;
          const latest = results[0];
          const latestId = Number((latest as { id?: unknown }).id);
          if (!Number.isFinite(latestId)) continue;
          if (baselineId !== null && latestId <= baselineId) continue;

          const tsRaw = String((latest as { timestamp?: unknown }).timestamp ?? '');
          const ts = Date.parse(tsRaw);
          if (!Number.isNaN(ts) && ts + 5_000 < startedAt) {
            // Existing historical result; keep polling.
            continue;
          }

          const detail = await fetchGazeResultDetail(latestId);
          const tasks = Array.isArray(detail.tasks)
            ? (detail.tasks as TaskResult[]).map((task) => ({
                task: task.task,
                label: task.label,
                confidence: task.confidence,
                prob: task.prob,
                threshold: task.threshold,
              }))
            : undefined;

          latestKnownResultIdRef.current = latestId;
          return {
            id: latestId,
            label: detail.label,
            confidence: detail.confidence,
            tasks,
          };
        } catch (pollError) {
          logDebug('[Done] polling for finalized result failed', pollError);
        }
      }
      return null;
    },
    [],
  );

  const attemptRecovery = useCallback(
    async (
      reason: unknown,
      baselineId: number | null,
      startedAt: number,
    ): Promise<DiagnosisResult | null> => {
      if (!isRecoverableError(reason)) return null;
      if (isMountedRef.current) setRecovering(true);
      try {
        return await pollForNewResult(baselineId, startedAt);
      } finally {
        if (isMountedRef.current) setRecovering(false);
      }
    },
    [pollForNewResult],
  );

  const cleanupAfterFallback = useCallback(() => {
    if (persistedFormId > 0) {
      try {
        localStorage.removeItem('argus_formDataId');
      } catch (cleanupError) {
        logDebug('[Done] failed to remove persisted form id during cleanup', cleanupError);
      }
    }
    void deleteVideoArtifact(clientRunId).catch((cleanupError) =>
      logDebug('[Done] failed to delete recovered video artifact during cleanup', cleanupError),
    );
    clearClientRunId();
    clearCollectionStorage();
    clear();
  }, [clear, clearCollectionStorage, clientRunId, persistedFormId]);

  const requiresVideoArtifact = isVideoWorkflow(workflow.runnerMode) || workflow.storesFullVideo;
  const hasVideoArtifact = Boolean(effectiveVideoBlob && effectiveVideoBytes > 0);
  const fakeVideoSubmit = DEMO_FAKE_VIDEO_UPLOAD && Boolean(collectionContext);
  const showVideoSubmitProgress = hasVideoArtifact || fakeVideoSubmit;
  const willFinalize =
    fakeVideoSubmit ||
    hasVideoArtifact ||
    (!requiresVideoArtifact && (persistedFormId > 0 || samples.length > 0));
  const sampleCount = samples.length;

  logDebug('[Done] mount state', {
    willFinalize,
    persistedFormId,
    sampleCount,
  });

  const finalizeExisting = useCallback(async (): Promise<DiagnosisResult> => {
    if (persistedFormId <= 0) {
      throw new Error('No persisted form to finalize');
    }

    try {
      await flushPendingIDB(persistedFormId, (count) => {
        logDebug('[Done] flushed pending landmarks', count);
      });
    } catch (flushError) {
      logDebug('[Done] flushing pending landmarks failed', flushError);
    }

    const payload = { static: staticInfo, timeline: timeline as TimelineSeg[] };
    const response = await finalizeChunkedUpload(persistedFormId, payload);
    try {
      localStorage.removeItem('argus_formDataId');
    } catch (removeError) {
      logDebug('[Done] failed to remove persisted form id', removeError);
    }
    clearCollectionStorage();
    clear();
    clearClientRunId();
    return attachName(response);
  }, [attachName, clear, clearCollectionStorage, persistedFormId, staticInfo, timeline]);

  const uploadSequential = useCallback(async (): Promise<DiagnosisResult> => {
    logDebug('[Done] sequential upload path', samples.length);
    const { formDataId } = await submitForm('web-run');
    const payload = { static: staticInfo, timeline: timeline as TimelineSeg[] };
    const response = await uploadSamplesSequential(
      formDataId,
      samples as EyeTraxSample[],
      {
        targetMaxBytes: MAX_PAYLOAD_BYTES,
        seedChunkSize: 80,
        onProgress: setProgress,
      },
      payload,
    );
    clearCollectionStorage();
    clear();
    clearClientRunId();
    return attachName(response);
  }, [attachName, clear, clearCollectionStorage, samples, staticInfo, timeline]);

  const uploadAll = useCallback(async () => {
    if (persistedFormId > 0) {
      return finalizeExisting();
    }
    return uploadSequential();
  }, [finalizeExisting, persistedFormId, uploadSequential]);

  const executeVideoSubmit = useCallback(async () => {
    setBusy(true);
    setError(null);
    setProgress(null);
    setVideoProgress(null);
    let baselineId: number | null = null;
    const startedAt = Date.now();
    try {
      if (DEMO_FAKE_VIDEO_UPLOAD) {
        if (!collectionContext) {
          throw new Error('缺少受试者与参考状态信息，请从“开始新测试”页面重新打开筛查流程。');
        }

        const totalBytes = effectiveVideoBytes > 0 ? effectiveVideoBytes : 1;
        setVideoProgress({
          loadedBytes: 0,
          totalBytes,
          percent: 0,
          phase: 'uploading',
        });
        await delay(450);
        if (!isMountedRef.current) return;
        setVideoProgress({
          loadedBytes: totalBytes,
          totalBytes,
          percent: 100,
          phase: 'processing',
        });
        await delay(650);
        if (!isMountedRef.current) return;

        const effectiveCaptureQc = captureQc ?? buildMissingCaptureQc();
        const summary =
          effectiveCaptureQc.passed === true
            ? buildDemoScreeningSummary(collectionContext)
            : buildQcFailedSummary(effectiveCaptureQc);
        const localResultId = persistDemoResultDetail(
          collectionContext,
          clientRunId,
          workflow,
          effectiveCaptureQc,
          captureTelemetry,
          timeline as TimelineSeg[],
          summary,
        );

        try {
          localStorage.removeItem('argus_formDataId');
        } catch (cleanupError) {
          logDebug('[Done] failed to clear persisted markers after fake video submit', cleanupError);
        }
        await deleteVideoArtifact(clientRunId).catch((cleanupError) =>
          logDebug('[Done] failed to delete persisted video artifact after fake submit', cleanupError),
        );

        clearCollectionStorage();
        const state = effectiveCaptureQc.passed ? 'local_generated' : 'local_qc_failed';
        const attached = attachName({
          id: localResultId,
          state,
          route: `/results/local/${localResultId}`,
          name: collectionContext.participant.name ?? undefined,
          label: summary.label || undefined,
          confidence: summary.confidence,
          tasks: summary.tasks,
        });
        setResult(attached);
        setBusy(false);
        setVideoProgress(null);
        clear();
        clearClientRunId();
        window.setTimeout(() => closeWindow(nav, attached), 200);
        return;
      }

      if (!effectiveVideoBlob || effectiveVideoBytes <= 0) {
        throw new Error('未检测到完整视频，请重新开始采集');
      }
      if (!collectionContext) {
        throw new Error('缺少受试者与参考状态信息，请从“开始新测试”页面重新打开筛查流程。');
      }

      setVideoProgress({
        loadedBytes: 0,
        totalBytes: effectiveVideoBytes,
        percent: 0,
        phase: 'uploading',
      });
      baselineId = await loadLatestResultId();
      const response = await submitVideoSession(collectionContext, effectiveVideoBlob, {
        timeline: timeline as TimelineSeg[],
        onProgress: (info) => {
          if (!isMountedRef.current) return;
          setVideoProgress(info);
        },
        meta: {
          mode: 'collect',
          client: 'projectargus',
          collectedAtClient: collectionContext.collectionMeta?.launchedAt,
          submittedAtClient: new Date().toISOString(),
          launcher: collectionContext.collectionMeta ?? null,
          clientRunId,
          workflowId: workflow.workflowId,
          workflowLabel: workflow.label,
          protocolId: workflow.protocolId,
          configPath: workflow.configPath,
          estimatedDurationSeconds: workflow.estimatedDurationSeconds,
          liveDiagnosis: workflow.liveDiagnosis,
          storesFullVideo: workflow.storesFullVideo,
          videoMimeType: effectiveVideoMimeType,
          videoBytes: effectiveVideoBytes,
          sampleCount: samples.length,
          captureQc,
          captureTelemetry,
        },
      });
      if (!isMountedRef.current) return;

      try {
        localStorage.removeItem('argus_formDataId');
      } catch (cleanupError) {
        logDebug('[Done] failed to clear persisted markers after video submit', cleanupError);
      }
      await deleteVideoArtifact(clientRunId).catch((cleanupError) =>
        logDebug('[Done] failed to delete persisted video artifact after submit', cleanupError),
      );

      clearCollectionStorage();
      const attached = attachName({
        id: response.id,
        state: response.state,
        name: response.participant?.name ?? undefined,
        label: response.label,
        confidence: response.confidence ?? undefined,
        tasks: response.tasks,
      });
      latestKnownResultIdRef.current = attached.id;
      setResult(attached);
      setBusy(false);
      setVideoProgress(null);
      clear();
      clearClientRunId();
      window.setTimeout(() => closeWindow(nav, attached), 200);
    } catch (submitError) {
      logDebug('[Done] video submit error', submitError);
      if (!isMountedRef.current) return;

      const recovered = await attemptRecovery(submitError, baselineId, startedAt);
      if (!isMountedRef.current) return;

      if (recovered) {
        cleanupAfterFallback();
        const attached = attachName(recovered);
        latestKnownResultIdRef.current = attached.id;
        setVideoProgress(null);
        setResult(attached);
        setBusy(false);
        window.setTimeout(() => closeWindow(nav, attached), 200);
        return;
      }

      setError(submitError instanceof Error ? submitError.message : '视频提交失败');
      setBusy(false);
      setVideoProgress(null);
    }
  }, [
    attachName,
    attemptRecovery,
    clear,
    cleanupAfterFallback,
    clearCollectionStorage,
    collectionContext,
    captureQc,
    captureTelemetry,
    clientRunId,
    effectiveVideoBlob,
    effectiveVideoBytes,
    effectiveVideoMimeType,
    loadLatestResultId,
    nav,
    samples.length,
    timeline,
    workflow,
  ]);

  useEffect(() => {
    if (videoBlob && videoBytes > 0) return;
    let cancelled = false;
    readVideoArtifact(clientRunId)
      .then((artifact) => {
        if (cancelled || !artifact) return;
        setRecoveredVideo({
          blob: artifact.blob,
          mimeType: artifact.mimeType,
          bytes: artifact.bytes || artifact.blob.size,
        });
      })
      .catch((readError) => logDebug('[Done] failed to read persisted video artifact', readError));
    return () => {
      cancelled = true;
    };
  }, [clientRunId, videoBlob, videoBytes]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const executeUpload = useCallback(async () => {
    setBusy(true);
    setError(null);
    setProgress(null);
    const baselineId = await loadLatestResultId();
    const startedAt = Date.now();
    try {
      const response = await uploadAll();

      logDebug('[Done] uploadAll result', response);
      if (!isMountedRef.current) return;

      latestKnownResultIdRef.current = response.id;
      setResult(response);
      setBusy(false);
      window.setTimeout(() => closeWindow(nav, response), 200);
    } catch (uploadError) {
      logDebug('[Done] upload error', uploadError);
      if (!isMountedRef.current) return;

      const recovered = await attemptRecovery(uploadError, baselineId, startedAt);
      if (!isMountedRef.current) return;

      if (recovered) {
        cleanupAfterFallback();
        const attached = attachName(recovered);
        latestKnownResultIdRef.current = attached.id;
        setProgress(null);
        setResult(attached);
        setBusy(false);
        window.setTimeout(() => closeWindow(nav, attached), 200);
        return;
      }

      setError(uploadError instanceof Error ? uploadError.message : 'Upload failed');
      setBusy(false);
    }
  }, [attachName, attemptRecovery, cleanupAfterFallback, loadLatestResultId, nav, uploadAll]);

  useEffect(() => {
    if (ran.current) return;

    if (!willFinalize) {
      ran.current = true;
      if (requiresVideoArtifact || collectionContext) {
        setError('本次视频未能在浏览器中保存完成。请使用最新版 Chrome、Edge 或 Safari 重新采集，并在上传完成前不要关闭页面。');
        return;
      }
      closeWindow(nav, null);
      return;
    }

    ran.current = true;
    if (hasVideoArtifact || fakeVideoSubmit) {
      void executeVideoSubmit();
      return;
    }

    void executeUpload();
  }, [collectionContext, executeUpload, executeVideoSubmit, fakeVideoSubmit, hasVideoArtifact, nav, requiresVideoArtifact, willFinalize]);

  return (
    <div className="full center stack">
      <Typography variant="h5" component="h2">
        已完成
      </Typography>

      {error ? (
        <>
          <Typography component="p" color="error">
            {error}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
                variant="outlined"
                onClick={() => {
                  clear();
                  nav(buildArgusRunnerPath('/runner/start', workflow.workflowId));
                }}
              >
                重新开始
            </Button>
            {willFinalize && (
              <Button
                variant="contained"
                onClick={() => {
                  if (hasVideoArtifact || fakeVideoSubmit) {
                    void executeVideoSubmit();
                  } else {
                    void executeUpload();
                  }
                }}
              >
                {hasVideoArtifact || fakeVideoSubmit ? '重试视频提交' : '重试上传'}
              </Button>
            )}
          </Stack>
        </>
      ) : result ? (
        <>
          <Typography variant="h6" component="h3">
            结果
          </Typography>
          {result.name && <Typography>姓名：{result.name}</Typography>}
          {result.label ? <Typography>标签：{result.label}</Typography> : null}
          {typeof result.confidence === 'number' ? (
            <Typography>置信度：{result.confidence.toFixed(2)}</Typography>
          ) : (
            <Typography>状态：采集完成</Typography>
          )}
          {Array.isArray(result.tasks) && result.tasks.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 1 }}>
                分项结果
              </Typography>
              <ul style={{ marginTop: 4 }}>
                {result.tasks.map((task) => (
                  <li key={task.task}>
                    <Typography variant="body2">
                      {task.task}：{task.label}（{task.confidence.toFixed(2)}）
                      {typeof task.prob === 'number' ? ` • 风险 ${task.prob.toFixed(2)}` : ''}
                    </Typography>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : (
        <>
          <Typography component="p" sx={{ opacity: 0.8 }}>
            {busy && showVideoSubmitProgress
              ? videoProgress?.phase === 'processing'
                ? DEMO_FAKE_VIDEO_UPLOAD
                  ? '视频处理完成，正在生成采集记录，请稍候…'
                  : '视频上传完成，正在保存采集记录，请稍候…'
                : `正在上传${workflow.label}的完整视频与采集元数据，请稍候…`
              : '正在上传结果，请稍候…'}
          </Typography>
          {busy && showVideoSubmitProgress ? (
            <LinearProgress
              sx={{ mt: 2, width: '100%' }}
              variant={videoProgress?.phase === 'uploading' && videoProgress.percent !== null ? 'determinate' : 'indeterminate'}
              value={videoProgress?.phase === 'uploading' ? Math.max(0, Math.min(100, videoProgress.percent ?? 0)) : undefined}
            />
          ) : busy ? (
            <LinearProgress sx={{ mt: 2, width: '100%' }} />
          ) : null}
          {progress && (
            <Typography variant="caption" sx={{ mt: 1 }}>
              已发送 {progress.sentChunks}/{progress.totalChunks} 块，共{' '}
              {Math.round(progress.bytes / 1024)} KB
            </Typography>
          )}
          {busy && showVideoSubmitProgress && videoProgress && (
            <Typography variant="caption" sx={{ mt: 1 }}>
              {videoProgress.phase === 'processing'
                ? DEMO_FAKE_VIDEO_UPLOAD
                  ? `已处理 ${formatBytes(videoProgress.totalBytes || effectiveVideoBytes)}，正在生成本次记录…`
                  : `已上传 ${formatBytes(videoProgress.totalBytes || effectiveVideoBytes)}，服务器正在写入并校验本次记录…`
                : `已上传 ${formatBytes(videoProgress.loadedBytes)} / ${formatBytes(videoProgress.totalBytes || effectiveVideoBytes)}${videoProgress.percent !== null ? ` (${Math.round(videoProgress.percent)}%)` : ''}`}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 2, opacity: 0.7 }}>
            {showVideoSubmitProgress
              ? '请保持页面打开，直到页面自动关闭或跳转到结果页。'
              : '最终结果生成可能需要 30–60 秒，请保持页面打开。'}
          </Typography>
          {recovering && (
            <Typography variant="caption" sx={{ mt: 1 }}>
              上传连接可能已中断，正在确认服务器是否已经保存本次记录，请勿关闭页面…
            </Typography>
          )}
        </>
      )}
    </div>
  );
}
